<template>
  <div v-if="showValidImageAvatar" class="profile_icon w-fit h-fit">
    <img class="rounded-full" :style="circleStyle" :src="avatarUrl" alt="avatar" @load="handleImageLoad" @error="handleImageError" v-show="imageLoaded" ref="imageRef" />
  </div>

  <div v-else-if="initialsContent !== '' && initialsContent !== 'N/A'" class="profile_icon" :class="getProfileColor(initialsContent)" :style="circleStyle">
    {{ initialsContent }}
  </div>

  <div v-else class="profile_icon profile_default_color" :style="circleStyle">
    <img class="size-6" :src="imagePath.profileIconPlaceholder" alt="profile_icon_placeholder" />
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { getInitials, getProfileColor, renderEntity, placeholderImagesToSkip } from '@/javascript/utils.js';
import imagePath from '@/javascript/imagePath';

const props = defineProps({
  data: { type: [Object, String], required: true },
  size: { type: [Number, String], default: 28 },
  font: { type: [Number, String], default: 12 },
  avatar: { type: String, default: null },
  view: { type: Boolean, default: false },
  param: { type: String, default: null },
});

const imageLoaded = ref(false);
const imageError = ref(false);
const imageRef = ref(null);

const isPlaceholderImage = (url) => {
  return placeholderImagesToSkip.some((placeholder) => url.includes(placeholder));
};

const showValidImageAvatar = computed(() => {
  if (imageError.value) return false;
  const url = avatarUrl.value;
  return !!(url && !isPlaceholderImage(url));
});

const avatarUrl = computed(() => {
  if (props.avatar) {
    return props.data[props.avatar];
  }
  if (!props.param && !props.view && !props.avatar) {
    return props.data?.logo_url || props.data?.profile_pic;
  }
  return '';
});

const initialsContent = computed(() => {
  if (props.view && props.param) {
    return getInitials(props.data[props.param]);
  }
  return getInitials(renderEntity(props.data));
});

const circleStyle = computed(() => {
  const sizeValue = typeof props.size === 'number' ? `${props.size}px` : props.size;
  const fontValue = typeof props.font === 'number' ? `${props.font}px` : props.font;
  return {
    minWidth: sizeValue,
    minHeight: sizeValue,
    width: sizeValue,
    height: sizeValue,
    fontSize: fontValue,
  };
});

const handleImageLoad = () => {
  imageLoaded.value = true;
  imageError.value = false;
};

const handleImageError = () => {
  imageLoaded.value = false;
  imageError.value = true;
};

onMounted(() => {
  if (imageRef.value && imageRef.value.complete) {
    if (imageRef.value.naturalWidth === 0) {
      handleImageError();
    } else {
      handleImageLoad();
    }
  }
});
</script>

<style scoped>
.profile_icon {
  border-radius: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  color: var(--foreground-rgb);
  text-align: center;
  font-family: 'Proxima Nova', sans-serif;
  font-style: normal;
  font-weight: 500;
}
</style>
